import { formatNumber } from './format';

export const getLinkedEventTypeLabel = (eventType, t) => {
  if (eventType === 'FINANCING') {
    return t ? t('linked.financing') : '贷款摘要';
  }
  if (eventType === 'STRUCTURED_EVENT') {
    return t ? t('linked.structured') : '结构性事件';
  }
  return 'FX';
};

export const getLinkedEventSecondaryText = (item, t) => {
  const currency = item.secondary_currency || item.currency || '-';
  const amount = formatNumber(item.secondary_amount, 2);
  if (item.event_type === 'FINANCING') {
    return t ? t('linked.interestLabel', { currency, amount }) : `利息 ${currency} ${amount}`;
  }
  if (item.event_type === 'STRUCTURED_EVENT') {
    return t ? t('linked.settleLabel', { currency, amount }) : `结算 ${currency} ${amount}`;
  }
  return t ? t('linked.buyLabel', { currency, amount }) : `买入 ${currency} ${amount}`;
};

// Linked-event notes are stored as source-document text.  Do not render that
// text verbatim: it may have been extracted in a different language from the
// currently selected UI language.  Rebuild the standard notes from structured
// event fields so switching languages also switches this part of the UI.
export const getLinkedEventNoteText = (item, t) => {
  if (!item) return '-';
  const currency = item.secondary_currency || item.currency || '-';
  const amount = formatNumber(item.secondary_amount, 2);

  if (item.event_type === 'FX') {
    return t ? t('panel.linked.note.fx', { currency }) : `Converted to ${currency}`;
  }

  if (item.event_type === 'FINANCING') {
    const stage = String(item.event_stage || '').toUpperCase();
    const key = stage === 'ROLLOVER'
      ? 'panel.linked.note.financingRollover'
      : stage === 'MATURITY'
        ? 'panel.linked.note.financingMaturity'
        : 'panel.linked.note.financingOpen';
    return t ? t(key, {
      eventDate: item.event_date || '-',
      startDate: item.end_date || '-',
      currency,
      amount
    }) : item.note || '-';
  }

  return item.note || '-';
};

export const getLedgerTypeLabel = (itemType, t) => {
  if (itemType === 'DIVIDEND') {
    return 'DIVIDEND';
  }
  if (itemType === 'CONVERSION_OUT') {
    return t ? t('lifecycle.conversionOut') : '转换转出';
  }
  if (itemType === 'CONVERSION_IN') {
    return t ? t('lifecycle.conversionIn') : '转换转入';
  }
  if (itemType === 'TERMINATION') {
    return t ? t('lifecycle.termination') : '提前终止';
  }
  return itemType;
};

export const analyticsPositionFilters = [
  { key: 'ALL', labelKey: 'position.all' },
  { key: 'CURRENT', labelKey: 'position.current' },
  { key: 'EXITED', labelKey: 'position.exited' }
];

export const assetPositionStatusKeys = {
  CURRENT: 'position.status.current',
  EXITED: 'position.status.exited',
  INACTIVE: 'position.status.inactive'
};

export const getAssetPositionStatusLabel = (status, t) => t ? t(assetPositionStatusKeys[status] || 'position.status.inactive') : (assetPositionStatusKeys[status] || 'position.status.inactive');

export const getAssetPositionStatus = (item) => {
  const rawStatus = String(item?.position_status || '').trim().toUpperCase();
  if (rawStatus === 'CURRENT' || rawStatus === 'EXITED' || rawStatus === 'INACTIVE') {
    return rawStatus;
  }
  if (Number(item?.current_quantity || 0) > 0.0001) {
    return 'CURRENT';
  }
  return item?.ever_held ? 'EXITED' : 'INACTIVE';
};

export const getPositionFilterLabel = (positionFilter, t) => {
  const filter = analyticsPositionFilters.find((item) => item.key === positionFilter);
  if (!filter) return t ? t('position.all') : '全部';
  return t ? t(filter.labelKey) : filter.label || '全部';
};

export const getPositionScopedAssets = (items, positionFilter) => {
  if (positionFilter === 'ALL') {
    return items;
  }
  return items.filter((item) => getAssetPositionStatus(item) === positionFilter);
};

export const buildCurrencyUsdRateMap = (assets) => assets.reduce((map, item) => {
  const currency = String(item?.currency || '').trim().toUpperCase();
  const fxRateToUsd = Number(item?.fx_rate_to_usd || 0);
  if (currency && fxRateToUsd > 0.0001 && map[currency] === undefined) {
    map[currency] = fxRateToUsd;
  }
  return map;
}, { USD: 1 });

export const mapFinancingInterestDetails = (details, assetSummaryByNo, currencyUsdRateMap) => {
  const normalizedNameLookup = Object.values(assetSummaryByNo || {}).reduce((map, asset) => {
    const nameKey = String(asset?.security_name || '').trim().toLowerCase();
    if (nameKey && asset?.asset_no && !map[nameKey]) {
      map[nameKey] = String(asset.asset_no);
    }
    return map;
  }, {});

  return details.map((item) => {
    const linkedAssetNo = String(item?.linked_asset_no || '').trim();
    const linkedNameKey = String(item?.linked_security_name || '').trim().toLowerCase();
    const resolvedAssetNo = linkedAssetNo || (linkedNameKey ? normalizedNameLookup[linkedNameKey] || '' : '');
    const matchedAsset = resolvedAssetNo ? assetSummaryByNo[resolvedAssetNo] : null;

    const currency = String(item?.currency || matchedAsset?.currency || 'USD').trim().toUpperCase() || 'USD';
    const fallbackRate = currency === 'USD' ? 1 : 0;
    const assetCurrency = String(matchedAsset?.currency || '').trim().toUpperCase();
    const assetFxRate = matchedAsset?.fx_rate_to_usd;
    const fxRateToUsd = assetCurrency === currency && assetFxRate
      ? Number(assetFxRate)
      : Number(currencyUsdRateMap[currency] || fallbackRate || 0);

    const interestAmount = Math.abs(Number(item?.interest_amount || 0));
    const providedUsd = Math.abs(Number(item?.interest_amount_usd || 0));
    const interestAmountUsd = providedUsd > 0.0001 ? providedUsd : (interestAmount * fxRateToUsd);

    return {
      ...item,
      currency,
      interest_amount: interestAmount,
      interest_amount_usd: interestAmountUsd,
      linked_asset_no: resolvedAssetNo,
      linked_position_status: matchedAsset ? getAssetPositionStatus(matchedAsset) : ''
    };
  });
};

export const getScopedFinancingInterestMetrics = (details, positionFilter, scopedAssets, totalInterestUsd, assetSummaryByNo) => {
  if (positionFilter === 'ALL') {
    return {
      scopedUsd: Number(totalInterestUsd || 0),
      matchedCount: details.length,
      matchedDetails: details,
      unassignedUsd: 0,
      unassignedCount: 0,
      unassignedDetails: [],
      displayDetails: details
    };
  }
  const scopedAssetNoSet = new Set(scopedAssets.map((item) => String(item?.asset_no || '').trim()).filter(Boolean));
  let scopedUsd = 0;
  let unassignedUsd = 0;
  const matchedDetails = [];
  const unassignedDetails = [];
  details.forEach((item) => {
    const linkedAssetNo = String(item?.linked_asset_no || '').trim();
    if (!linkedAssetNo) {
      unassignedUsd += Number(item?.interest_amount_usd || 0);
      unassignedDetails.push(item);
      return;
    }
    if (scopedAssetNoSet.has(linkedAssetNo)) {
      scopedUsd += Number(item?.interest_amount_usd || 0);
      matchedDetails.push(item);
      return;
    }
    unassignedUsd += Number(item?.interest_amount_usd || 0);
    unassignedDetails.push(item);
  });
  return {
    scopedUsd,
    matchedCount: matchedDetails.length,
    matchedDetails,
    unassignedUsd,
    unassignedCount: unassignedDetails.length,
    unassignedDetails,
    displayDetails: [...matchedDetails, ...unassignedDetails]
  };
};
